import { Badge, Space, Tag, Tooltip, Typography } from 'antd';
import { LockOutlined, RobotOutlined, UserOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import type { ConversationMessage, ConversationThread } from '@/api/conversationApi';

const { Paragraph, Text } = Typography;

export function formatTime(value: string | null): string {
  if (!value) return '—';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? value : date.toLocaleString();
}

/**
 * 中文：把等待秒數格式化為人看得懂的相對時間，客服一眼判斷急迫度。
 * English: Formats a waiting duration into a human-readable relative time so urgency is obvious at a glance.
 */
export function formatWaiting(since: string | null, now: number): string {
  if (!since) return '—';
  const start = new Date(since).getTime();
  if (Number.isNaN(start)) return '—';
  const seconds = Math.max(0, Math.floor((now - start) / 1000));
  if (seconds < 60) return `${seconds}s`;
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ${minutes % 60}m`;
  return `${Math.floor(hours / 24)}d ${hours % 24}h`;
}

/**
 * 中文：等待越久顏色越警示；門檻刻意保守，避免整片紅字讓客服失去敏感度。
 * English: The longer the wait the stronger the colour. Thresholds stay conservative so a wall of red does not
 *          desensitise the agent.
 */
export function waitingColor(since: string | null, now: number): string | undefined {
  if (!since) return undefined;
  const start = new Date(since).getTime();
  if (Number.isNaN(start)) return undefined;
  const minutes = (now - start) / 60000;
  if (minutes >= 30) return '#cf1322';
  if (minutes >= 10) return '#d46b08';
  return undefined;
}

const PRIORITY_COLORS: Record<string, string> = {
  low: 'default',
  normal: 'blue',
  high: 'orange',
  urgent: 'red',
};

export interface ThreadListItemProps {
  thread: ConversationThread;
  selected: boolean;
  now: number;
  projectName: string;
  agentSubjectId: string;
  onSelect: (thread: ConversationThread) => void;
}

export function ThreadListItem({
  thread,
  selected,
  now,
  projectName,
  agentSubjectId,
  onSelect,
}: ThreadListItemProps) {
  const { t } = useTranslation();
  const mine = thread.assignedAgentId === agentSubjectId;
  const waitColor = thread.awaitingReply ? waitingColor(thread.waitingSince, now) : undefined;

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={() => onSelect(thread)}
      onKeyDown={(event) => {
        if (event.key === 'Enter' || event.key === ' ') onSelect(thread);
      }}
      style={{
        cursor: 'pointer',
        padding: '12px 16px',
        borderBottom: '1px solid #f0f0f0',
        background: selected ? '#e6f4ff' : undefined,
        borderLeft: `3px solid ${thread.awaitingReply ? waitColor ?? '#1677ff' : 'transparent'}`,
      }}
    >
      <Space style={{ width: '100%', justifyContent: 'space-between' }} align="start">
        <Space size={6} wrap>
          {thread.hasUnread && <Badge status="error" />}
          <Text strong ellipsis style={{ maxWidth: 190 }}>
            {thread.title || thread.ownerSubjectId}
          </Text>
        </Space>
        {thread.awaitingReply && (
          <Text style={{ color: waitColor, fontVariantNumeric: 'tabular-nums' }}>
            {formatWaiting(thread.waitingSince ?? thread.lastMessageAt, now)}
          </Text>
        )}
      </Space>

      <Paragraph
        type="secondary"
        ellipsis={{ rows: 2 }}
        style={{ marginTop: 4, marginBottom: 6, fontSize: 12 }}
      >
        {thread.lastMessagePreview || t('conversations.no_messages')}
      </Paragraph>

      <Space size={4} wrap>
        <Tag color={thread.conversationType === 'support' ? 'gold' : 'blue'}>
          {t(`conversations.${thread.conversationType}`)}
        </Tag>
        <Tag color="geekblue">{projectName}</Tag>
        {thread.priority !== 'normal' && (
          <Tag color={PRIORITY_COLORS[thread.priority]}>
            {t(`conversations.priority_${thread.priority}`)}
          </Tag>
        )}
        {thread.assignedAgentId ? (
          <Tag icon={<UserOutlined />} color={mine ? 'green' : 'default'}>
            {mine ? t('conversations.assigned_to_me') : thread.assignedAgentId}
          </Tag>
        ) : (
          <Tag color="purple">{t('conversations.unassigned')}</Tag>
        )}
        {thread.lockedBy && thread.lockedBy !== agentSubjectId && (
          <Tooltip title={t('conversations.locked_by', { agent: thread.lockedBy })}>
            <Tag icon={<LockOutlined />} color="volcano">
              {t('conversations.locked_short')}
            </Tag>
          </Tooltip>
        )}
        {(thread.tags ?? []).map((tag) => (
          <Tag key={tag}>{tag}</Tag>
        ))}
      </Space>
    </div>
  );
}

const ROLE_STYLES: Record<string, { background: string; border: string; align: string }> = {
  user: { background: '#f5f5f5', border: '#8c8c8c', align: 'flex-start' },
  agent: { background: '#e6f4ff', border: '#1677ff', align: 'flex-end' },
  assistant: { background: '#e6fffb', border: '#13c2c2', align: 'flex-end' },
  system: { background: '#fafafa', border: '#d9d9d9', align: 'center' },
};

export function MessageBubble({ message }: { message: ConversationMessage }) {
  const { t } = useTranslation();
  const internal = message.visibility === 'internal';
  const style = ROLE_STYLES[message.senderRole] ?? ROLE_STYLES.system;

  return (
    <div style={{ display: 'flex', justifyContent: style.align, marginBottom: 12 }}>
      <div
        style={{
          maxWidth: '82%',
          padding: 12,
          borderRadius: 8,
          // 中文：內部備註以黃底明確區隔，避免客服誤以為客戶看得到。
          // English: Internal notes get a distinct amber ground so an agent never mistakes them for customer-visible.
          background: internal ? '#fffbe6' : style.background,
          borderLeft: `4px solid ${internal ? '#faad14' : style.border}`,
        }}
      >
        <Space wrap size={6}>
          <Tag
            icon={message.senderRole === 'assistant' ? <RobotOutlined /> : undefined}
            color={message.senderRole === 'assistant' ? 'cyan' : undefined}
          >
            {t(`conversations.role_${message.senderRole}`)}
          </Tag>
          {internal && <Tag color="warning">{t('conversations.internal_note_badge')}</Tag>}
          <Text type="secondary" style={{ fontSize: 12 }}>
            {message.senderSubjectId}
          </Text>
          <Text type="secondary" style={{ fontSize: 12 }}>
            {formatTime(message.createdAt)}
          </Text>
          {message.providerRevision && (
            <Tooltip title={t('conversations.ai_model_hint')}>
              <Tag color="purple">{message.providerRevision}</Tag>
            </Tooltip>
          )}
        </Space>
        <Paragraph style={{ marginTop: 8, marginBottom: 0, whiteSpace: 'pre-wrap' }}>
          {message.content}
        </Paragraph>
      </div>
    </div>
  );
}
